import React, { useEffect, useState, useMemo, useCallback } from "react";
import api from "./api";

/* ===============================
   MAIN COMPONENT
================================ */
export default function PatientIrregular() {
  const today = new Date();
  const fromDate = new Date();
  fromDate.setMonth(today.getMonth() - 4);

  const [from, setFrom] = useState(fromDate.toISOString().slice(0, 10));
  const [to, setTo] = useState(today.toISOString().slice(0, 10));
  const [patients, setPatients] = useState([]);
  const [selected, setSelected] = useState(null);

  const [summaryFilter, setSummaryFilter] = useState(null);
  const [therapistFilter, setTherapistFilter] = useState("ALL");
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [kitData, setKitData] = useState({});
  const [kitLoading, setKitLoading] = useState({});

  const fetchKitData = useCallback(async (phone) => {
    if (!phone || kitData[phone] !== undefined) return;
    setKitLoading(prev => ({ ...prev, [phone]: true }));
    try {
      const res = await api.get(process.env.REACT_APP_URL + `/callleads/profiles/number/${phone}`);
      setKitData(prev => ({ ...prev, [phone]: res.data || null }));
    } catch {
      setKitData(prev => ({ ...prev, [phone]: null }));
    } finally {
      setKitLoading(prev => ({ ...prev, [phone]: false }));
    }
  }, [kitData]);

  const toggleExpand = useCallback((p) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(p.patientCard)) {
        next.delete(p.patientCard);
      } else {
        next.add(p.patientCard);
        fetchKitData(p.phone);
      }
      return next;
    });
  }, [fetchKitData]);

  useEffect(() => {
    api
      .get(process.env.REACT_APP_URL + "/appointments/type/Therapy/all", {
        params: { from, to }
      })
      .then(res => {
        const grouped = groupByPatient(res.data || []);
        const enriched = grouped.map(p => ({
          ...p,
          risk: calculatePatientRisk(p)
        }));
        enriched.sort((a, b) => b.risk.score - a.risk.score);
        setPatients(enriched);
      })
      .catch(console.error);
  }, [from, to]);

    /* ===============================
     FILTERED PATIENTS
  ================================ */
 const visiblePatients = useMemo(() => {
  return patients.filter(p => {
    if (summaryFilter === "MISSED" && p.risk.missed === 0) return false;
    if (summaryFilter === "HIGH" && p.risk.level !== "HIGH") return false;
    if (summaryFilter === "ONGOING_STREAK") {  return p.risk.ongoingMissStreak > 0; }

    if (summaryFilter === "MISSED_NOCOMMENTS") {
      return p.appointments.some(
        a =>
          (a.status.toLowerCase() === "skipped" ||
            (a.status.toLowerCase() === "scheduled" && new Date(a.date) < today)) &&
          !a.sessionComments
      );
    }

    if (therapistFilter !== "ALL") {
      return p.appointments.some(a => a.username === therapistFilter);
    }
    return true;
  });
}, [patients, summaryFilter, therapistFilter]);


  /* ===============================
     SUMMARY (DERIVED)
  ================================ */
  const summary = useMemo(() => {
    const all = visiblePatients.flatMap(p => p.appointments);
    const completed = all.filter(a => a.status.toLowerCase() === "completed").length;
    const missed = all.filter(
      a =>
        a.status.toLowerCase() === "skipped" ||
        (a.status.toLowerCase() === "scheduled" && new Date(a.date) < today)
    ).length;

    return {
      patients: visiblePatients.length,
      sessions: all.length,
      completed,
      missed,
      highRisk: visiblePatients.filter(p => p.risk.level === "HIGH").length
    };
  }, [visiblePatients]);

  /* ===============================
     THERAPIST FILTER (DERIVED)
  ================================ */
  const therapists = useMemo(() => {
    const set = new Set();
    patients.forEach(p => {
      if (p.risk.level === "HIGH" || p.risk.missed > 0) {
        p.appointments.forEach(a => {
          if (a.username) set.add(a.username);
        });
      }
    });
    return ["ALL", ...Array.from(set)];
  }, [patients]);

  /* ===============================
   MISSED WITHOUT COMMENTS SUMMARY
=============================== */
const missedNoCommentsCount = useMemo(() => {
  return visiblePatients.reduce((count, p) => {
    return (
      count +
      p.appointments.filter(
        a =>
          (a.status.toLowerCase() === "skipped" ||
            (a.status.toLowerCase() === "scheduled" && new Date(a.date) < today)) &&
          !a.sessionComments
      ).length
    );
  }, 0);
}, [visiblePatients]);

/* ===============================
   MISSED STREAK SUMMARY
=============================== */
const ongoingMissedStreakCount = useMemo(() => {
  return visiblePatients.reduce((sum, p) => sum + p.risk.ongoingMissStreak, 0);
}, [visiblePatients]);


  return (
    <div style={{ padding: 16 }}>
      <h2>🚨 Patient Attendance Risk Dashboard</h2>

      {/* FILTERS */}
      <div style={{ marginBottom: 12 }}>
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        <input
          type="date"
          value={to}
          onChange={e => setTo(e.target.value)}
          style={{ marginLeft: 8 }}
        />

        <select
          value={therapistFilter}
          onChange={e => setTherapistFilter(e.target.value)}
          style={{ marginLeft: 8 }}
        >
          {therapists.map(t => (
            <option key={t}>{t}</option>
          ))}
        </select>
      </div>

      {/* SUMMARY CARDS */}
      <div style={styles.summaryRow}>
        <SummaryCard label="Patients" value={summary.patients} />
        <SummaryCard label="Sessions" value={summary.sessions} />
        <SummaryCard label="Completed" value={summary.completed} />

        <SummaryCard
          label="Missed"
          value={summary.missed}
          active={summaryFilter === "MISSED"}
          onClick={() =>
            setSummaryFilter(summaryFilter === "MISSED" ? null : "MISSED")
          }
        />

        <SummaryCard
            label="Ongoing Missed Streaks"
            value={ongoingMissedStreakCount}
            alert
            active={summaryFilter === "ONGOING_STREAK"}
            onClick={() =>
                setSummaryFilter(
                summaryFilter === "ONGOING_STREAK" ? null : "ONGOING_STREAK"
                )
            }
            />


        <SummaryCard
          label="High Risk"
          value={summary.highRisk}
          alert
          active={summaryFilter === "HIGH"}
          onClick={() =>
            setSummaryFilter(summaryFilter === "HIGH" ? null : "HIGH")
          }
        />

        <SummaryCard
            label="Missed w/o Comments"
            value={missedNoCommentsCount}
            alert
            active={summaryFilter === "MISSED_NOCOMMENTS"}
            onClick={() =>
                setSummaryFilter(
                summaryFilter === "MISSED_NOCOMMENTS" ? null : "MISSED_NOCOMMENTS"
                )
            }
        />

      </div>

      {/* WEEKLY LINE CHART */}
      <WeeklyAttendanceLine patients={visiblePatients} />

      {/* PATIENT ROWS */}
      {visiblePatients.map(p => {
        const isExpanded = expandedCards.has(p.patientCard);
        const situation = getRiskSituation(p.risk, p.appointments);
        return (
          <div key={p.patientCard} style={{ marginBottom: 12 }}>
            {/* CARD HEADER */}
            <div
              onClick={() => toggleExpand(p)}
              style={{
                borderLeft: `8px solid ${p.risk.color}`,
                padding: "12px 14px",
                borderRadius: isExpanded ? "6px 6px 0 0" : 6,
                background: p.risk.level === "HIGH" ? "#fff3f3" : "#fff",
                cursor: "pointer",
                boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                userSelect: "none",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
                  <strong style={{ fontSize: 15 }}>{p.patientName}</strong>
                  <span style={{ fontSize: 11, fontWeight: 700, color: p.risk.color, background: p.risk.level === "HIGH" ? "#ffebee" : p.risk.level === "MEDIUM" ? "#fff3e0" : "#e8f5e9", padding: "2px 8px", borderRadius: 12 }}>
                    {p.risk.level} RISK
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: situation.color, background: situation.bg, padding: "2px 8px", borderRadius: 12 }}>
                    {situation.label}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: "#666", display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <span>❌ Missed: <b style={{ color: p.risk.missed > 0 ? "#d32f2f" : "#333" }}>{p.risk.missed}</b></span>
                  <span>🔥 Streak: <b style={{ color: p.risk.ongoingMissStreak >= 2 ? "#d32f2f" : "#333" }}>{p.risk.ongoingMissStreak}</b></span>
                  <span style={{ color: p.risk.stoppedDays > 14 ? "#d32f2f" : "#666" }}>
                    🕐 {p.risk.stoppedDays >= 60 ? "Never attended" : `Last: ${p.risk.stoppedDays}d ago`}
                  </span>
                  <span style={{ color: "#999" }}>📅 {p.appointments.length} sessions</span>
                </div>
              </div>
              <span style={{ fontSize: 16, color: "#aaa", marginLeft: 12, flexShrink: 0 }}>
                {isExpanded ? "▲" : "▼"}
              </span>
            </div>

            {/* EXPANDED ROW */}
            {isExpanded && (
              <ExpandedRow
                patient={p}
                calllead={kitData[p.phone]}
                loading={!!kitLoading[p.phone]}
              />
            )}
          </div>
        );
      })}

      {/* MODAL (detail view via risk score badge) */}
      {selected && (
        <div style={styles.backdrop} onClick={() => setSelected(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3>{selected.patientName}</h3>
            <MiniTrendChart appointments={selected.appointments} />
            <div style={{ marginTop: 10 }}>Risk Score: <b>{selected.risk.score}/100</b></div>
            <div style={{ marginTop: 14, textAlign: "right" }}>
              <button onClick={() => setSelected(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ===============================
   SUMMARY CARD
================================ */
function SummaryCard({ label, value, alert, active, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: 12,
        minWidth: 120,
        borderRadius: 6,
        background: active ? "#e3f2fd" : alert ? "#ffecec" : "#f5f5f5",
        textAlign: "center",
        cursor: onClick ? "pointer" : "default",
        border: active ? "2px solid #1976d2" : "none"
      }}
    >
      <div style={{ fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: "bold" }}>{value}</div>
    </div>
  );
}

/* ===============================
   WEEKLY LINE CHART
================================ */
function WeeklyAttendanceLine({ patients }) {
  const containerRef = React.useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [hover, setHover] = useState(null);
  const [width, setWidth] = useState(900);

  /* -------------------------
     RESPONSIVE WIDTH
  -------------------------- */
  useEffect(() => {
    if (!containerRef.current) return;
    const resize = () => {
      setWidth(Math.min(containerRef.current.offsetWidth, 1100));
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  /* -------------------------
     DATE RANGE & BUCKETING
  -------------------------- */
  const { data, bucket } = useMemo(() => {
    const appts = patients.flatMap(p => p.appointments);
    if (!appts.length) return { data: [], bucket: "week" };

    const dates = appts.map(a => new Date(a.date));
    const min = new Date(Math.min(...dates));
    const max = new Date(Math.max(...dates));
    const spanDays = Math.ceil((max - min) / 86400000);

    let bucket;
    if (spanDays <= 14) bucket = "day";
    else if (spanDays <= 90) bucket = "week";
    else bucket = "month";

    const map = {};

    appts.forEach(a => {
      const d = new Date(a.date);
      let key;

      if (bucket === "day") {
        key = d.toISOString().slice(0, 10);
      } else if (bucket === "week") {
        const w = new Date(d);
        w.setDate(d.getDate() - d.getDay());
        key = w.toISOString().slice(0, 10);
      } else {
        key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      }

      if (!map[key]) map[key] = { key, completed: 0, missed: 0 };

      if (a.status.toLowerCase() === "completed") map[key].completed++;
      if (
        a.status.toLowerCase() === "skipped" ||
        (a.status.toLowerCase() === "scheduled" && d < new Date())
      ) map[key].missed++;
    });

    return {
      bucket,
      data: Object.values(map).sort((a, b) => a.key.localeCompare(b.key))
    };
  }, [patients]);

  if (!data.length) return null;

  /* -------------------------
     CHART SCALES
  -------------------------- */
  const height = expanded ? 300 : 170;
  const padding = { l: 50, r: 20, t: 20, b: 40 };

  const maxY = Math.max(
    ...data.map(d => Math.max(d.completed, d.missed)),
    1
  );

  const scaleX = i =>
    padding.l +
    (i / (data.length - 1 || 1)) * (width - padding.l - padding.r);

  const scaleY = v =>
    height - padding.b - (v / maxY) * (height - padding.t - padding.b);

  const pathFor = key =>
    data
      .map((d, i) =>
        `${i === 0 ? "M" : "L"} ${scaleX(i)} ${scaleY(d[key])}`
      )
      .join(" ");

  /* -------------------------
     TOOLTIP POSITION FIX
  -------------------------- */
  const tooltipStyle = hover && containerRef.current
    ? (() => {
        const rect = containerRef.current.getBoundingClientRect();
        return {
          left: rect.left + hover.x + 12,
          top: rect.top + hover.y - 10
        };
      })()
    : {};

  /* -------------------------
     AXIS LABEL DENSITY
  -------------------------- */
  const labelStep =
    data.length > 18 ? Math.ceil(data.length / 8) : 1;

  return (
    <div ref={containerRef} style={{ margin: "18px 0", position: "relative" }}>
      {/* HEADER */}
      <div
        style={{ display: "flex", justifyContent: "space-between", cursor: "pointer" }}
        onClick={() => setExpanded(!expanded)}
      >
        <b>📈 Attendance Trend ({bucket})</b>
        <span style={{ fontSize: 12 }}>{expanded ? "▾ Collapse" : "▸ Expand"}</span>
      </div>

      {/* LEGEND */}
      <div style={{ fontSize: 12, margin: "6px 0" }}>
        <span style={{ color: "#4caf50" }}>● Completed</span>
        <span style={{ color: "#d32f2f", marginLeft: 14 }}>● Missed</span>
        
      </div>

      {/* SVG */}
      <svg width={width} height={height}>
        {/* Y GRID */}
        {[0, maxY].map(v => (
          <g key={v}>
            <line
              x1={padding.l}
              x2={width - padding.r}
              y1={scaleY(v)}
              y2={scaleY(v)}
              stroke="#eee"
            />
            <text x={8} y={scaleY(v) + 4} fontSize="10" fill="#666">
              {v}
            </text>
          </g>
        ))}

        {/* LINES */}
        <path d={pathFor("completed")} fill="none" stroke="#4caf50" strokeWidth="2" />
        <path d={pathFor("missed")} fill="none" stroke="#d32f2f" strokeWidth="2" />

        {/* POINTS */}
        {data.map((d, i) => (
          ["completed", "missed"].map(type => {
            const x = scaleX(i);
            const y = scaleY(d[type]);
            return (
              <circle
                key={type + d.key}
                cx={x}
                cy={y}
                r={4}
                fill={type === "completed" ? "#4caf50" : "#d32f2f"}
                onMouseEnter={() => setHover({ ...d, type, x, y })}
                onMouseLeave={() => setHover(null)}
              />
            );
          })
        ))}

        {/* X LABELS */}
        {data.map((d, i) =>
          i % labelStep === 0 ? (
            <text
              key={d.key}
              x={scaleX(i)}
              y={height - 10}
              fontSize="10"
              textAnchor="middle"
            >
              {bucket === "month" ? d.key : d.key.slice(5)}
            </text>
          ) : null
        )}
      </svg>

      {/* TOOLTIP */}
      {hover && (
        <div
          style={{
            position: "fixed",
            background: "#fff",
            border: "1px solid #ccc",
            padding: "6px 8px",
            fontSize: 12,
            borderRadius: 4,
            pointerEvents: "none",
            zIndex: 9999,
            ...tooltipStyle
          }}
        >
          <div><b>{hover.key}</b></div>
          <div>
            {hover.type === "completed" ? "✅ Completed" : "❌ Missed"}:{" "}
            {hover[hover.type]}
          </div>
        </div>
      )}
    </div>
  );
}



/* ===============================
   MINI TREND
================================ */
function MiniTrendChart({ appointments }) {
  const today = new Date();

  const sorted = appointments
    .slice()
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const window = [];
  for (const a of sorted) {
    const isMissed =
      a.status.toLowerCase() === "skipped" ||
      (a.status.toLowerCase() === "scheduled" && new Date(a.date) < today);

    window.push(a);
    if (a.status.toLowerCase() === "completed") break;
  }

  const completed = window.filter(a => a.status.toLowerCase() === "completed").length;
  const missed = window.length - completed;
  const total = completed + missed || 1;

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 13 }}>📊 Ongoing Attendance Trend</div>
      <div style={{ display: "flex", height: 14, borderRadius: 6 }}>
        <div style={{ width: `${(completed / total) * 100}%`, background: "#4caf50" }} />
        <div style={{ width: `${(missed / total) * 100}%`, background: "#d32f2f" }} />
      </div>
      <div style={{ fontSize: 12 }}>
        ✅ {completed} | ❌ {missed}
      </div>
    </div>
  );
}


/* ===============================
   HELPERS
================================ */
function groupByPatient(appointments) {
  const map = {};
  appointments.forEach(a => {
    if (!map[a.patientCard]) {
      map[a.patientCard] = {
        patientCard: a.patientCard,
        patientName: a.name,
        phone: a.number,
        appointments: []
      };
    }
    map[a.patientCard].appointments.push(a);
  });
  return Object.values(map);
}

function calculatePatientRisk(patient) {
  const today = new Date();

  let missed = 0;
  let lastVisit = null;
  let ongoingMissStreak = 0;

  // Sort newest → oldest
  const sorted = patient.appointments
    .slice()
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  for (const a of sorted) {
    const isMissed =
      a.status.toLowerCase() === "skipped" ||
      (a.status.toLowerCase() === "scheduled" && new Date(a.date) < today);

    if (isMissed) {
      missed++;
      ongoingMissStreak++;
    } else if (a.status.toLowerCase() === "completed") {
      const d = new Date(a.date);
      if (!lastVisit) lastVisit = d;

      // 🔴 stop counting streak once we hit a completed session
      break;
    }
  }

  const stoppedDays = lastVisit
    ? Math.floor((today - lastVisit) / 86400000)
    : 60;

  // ---------------------------
  // 🎯 RISK RULE (UPDATED)
  // ---------------------------
  let level = "LOW";
  let color = "#4caf50";

  if (ongoingMissStreak >= 2) {
    level = "HIGH";
    color = "#d32f2f";
  } else if (missed > 0 || stoppedDays >= 14) {
    level = "MEDIUM";
    color = "#ff9800";
  }

  // Score is now secondary (for sorting only)
  const score =
    ongoingMissStreak * 40 +
    missed * 10 +
    Math.min(stoppedDays, 60) * 0.5;

  return {
    score: Math.round(score),
    level,
    color,
    missed,
    stoppedDays,
    ongoingMissStreak
  };
}

/* ===============================
   RISK SITUATION CLASSIFIER
================================ */
function getRiskSituation(risk, appointments) {
  const { ongoingMissStreak, stoppedDays, missed } = risk;
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000);

  const hasConcerns = appointments.some(a =>
    (a.commentsByPt || "").trim().length > 0 && new Date(a.date) > thirtyDaysAgo
  );

  if (hasConcerns)            return { label: "🚩 Flagging Concerns",       color: "#7b1fa2", bg: "#f3e5f5" };
  if (stoppedDays > 45 && missed > 0)
                              return { label: "🕳️ Silent Dropout",          color: "#b71c1c", bg: "#ffebee" };
  if (ongoingMissStreak >= 3) return { label: "🚨 Dropping Out",            color: "#d32f2f", bg: "#ffebee" };
  if (ongoingMissStreak >= 2) return { label: "⚠️ Missing Sessions",        color: "#f57c00", bg: "#fff3e0" };
  if (stoppedDays > 21 && missed > 0)
                              return { label: "📉 Gradually Slipping",      color: "#e65100", bg: "#fff3e0" };
  if (ongoingMissStreak === 1)return { label: "😕 Just Missed Once",        color: "#f9a825", bg: "#fffde7" };
  if (missed === 0 && stoppedDays < 10)
                              return { label: "✅ On Track",                color: "#2e7d32", bg: "#e8f5e9" };
  if (missed === 0)           return { label: "🟢 Attending",               color: "#388e3c", bg: "#f1f8e9" };
  return                             { label: "📊 Monitoring",              color: "#1565c0", bg: "#e3f2fd" };
}

/* ===============================
   EXPANDED ROW
================================ */
function ExpandedRow({ patient, calllead, loading }) {
  const { risk, appointments, phone } = patient;
  const today = new Date();

  const sortedAppts = [...appointments].sort((a, b) => new Date(a.date) - new Date(b.date));
  const completedSessions = sortedAppts.filter(a => a.status.toLowerCase() === "completed").length;
  const lastAttended = risk.stoppedDays >= 60 ? null : risk.stoppedDays;

  /* Build unified comm timeline */
  const sessionComms = sortedAppts
    .filter(a => {
      const isMissed = a.status.toLowerCase() === "skipped" ||
        (a.status.toLowerCase() === "scheduled" && new Date(a.date) < today);
      return isMissed || a.status.toLowerCase() === "completed";
    })
    .map(a => {
      const isMissed = a.status.toLowerCase() === "skipped" ||
        (a.status.toLowerCase() === "scheduled" && new Date(a.date) < today);
      return {
        date: new Date(a.date),
        kind: "session",
        status: isMissed ? "missed" : "completed",
        text: a.sessionComments || "",
        by: a.leadTherapist || a.username || "",
      };
    });

  const waComms = (calllead?.messageHistory || []).map(m => ({
    date: new Date(m.date),
    kind: "whatsapp",
    text: m.text || "",
    msgType: m.type || "",
  }));

  const fupComms = (calllead?.followUpHistory || []).map(f => ({
    date: new Date(f.date || f.updatedAt),
    kind: "followup",
    text: f.comments || f.enquiryDetails || "",
  }));

  const allComms = [...sessionComms, ...waComms, ...fupComms]
    .sort((a, b) => b.date - a.date)
    .slice(0, 15);

  return (
    <div style={{
      borderLeft: "8px solid #e0e0e0",
      borderRadius: "0 0 6px 6px",
      background: "#f8f9fa",
      padding: "14px 16px 16px",
      boxShadow: "0 3px 8px rgba(0,0,0,0.1)",
    }}>
      {/* PHONE + ACTIONS + LAST ATTENDED */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: "#333" }}>📱 {phone || "—"}</span>
        {phone && (
          <>
            <a href={`tel:${phone}`} style={actionBtnStyle("#1976d2")}>📞 Call</a>
            <a href={`https://wa.me/91${String(phone).replace(/\D/g, "")}`} target="_blank" rel="noreferrer" style={actionBtnStyle("#25D366")}>
              💬 WhatsApp
            </a>
          </>
        )}
        {lastAttended !== null ? (
          <span style={{
            padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700,
            background: lastAttended > 21 ? "#ffebee" : lastAttended > 10 ? "#fff3e0" : "#e8f5e9",
            color: lastAttended > 21 ? "#b71c1c" : lastAttended > 10 ? "#e65100" : "#2e7d32",
          }}>
            🕐 Last attended {lastAttended === 0 ? "today" : `${lastAttended}d ago`}
          </span>
        ) : (
          <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 12, background: "#f3e5f5", color: "#6a1b9a", fontWeight: 700 }}>
            ⚠️ Never attended
          </span>
        )}
        {calllead?.kitActive && (
          <span style={{ padding: "3px 10px", borderRadius: 20, fontSize: 12, background: "#e0f2f1", color: "#00695c", fontWeight: 700 }}>
            🚀 KIT Active
          </span>
        )}
      </div>

      {/* SESSION TIMELINE */}
      <SessionTimeline appointments={sortedAppts} completedSessions={completedSessions} today={today} />

      {/* COMMUNICATION TIMELINE */}
      <div style={{ marginTop: 18 }}>
        <div style={sectionTitleStyle}>💬 RECENT COMMUNICATIONS</div>
        {loading ? (
          <div style={{ fontSize: 12, color: "#999", fontStyle: "italic", padding: "8px 0" }}>
            Fetching KIT messages…
          </div>
        ) : allComms.length === 0 ? (
          <div style={{ fontSize: 12, color: "#bbb", fontStyle: "italic", padding: "8px 0" }}>
            No communication history found.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {allComms.map((item, i) => <CommItem key={i} item={item} />)}
          </div>
        )}
      </div>
    </div>
  );
}

/* ===============================
   SESSION TIMELINE CHART
================================ */
function SessionTimeline({ appointments, completedSessions, today }) {
  const total = appointments.length;
  const pct = total > 0 ? Math.round((completedSessions / total) * 100) : 0;
  const missedCount = appointments.filter(a =>
    a.status.toLowerCase() === "skipped" ||
    (a.status.toLowerCase() === "scheduled" && new Date(a.date) < today)
  ).length;

  return (
    <div style={{ background: "#fff", borderRadius: 8, padding: "12px 14px", border: "1px solid #e8e8e8" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <span style={sectionTitleStyle}>📊 SESSION PROGRESS</span>
        <span style={{ fontSize: 15, fontWeight: 800, color: pct >= 70 ? "#2e7d32" : pct >= 40 ? "#e65100" : "#b71c1c" }}>
          {completedSessions}/{total} sessions
        </span>
      </div>

      {/* Dot timeline — each appointment = one dot */}
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 10 }}>
        {appointments.map((a, i) => {
          const isMissed = a.status.toLowerCase() === "skipped" ||
            (a.status.toLowerCase() === "scheduled" && new Date(a.date) < today);
          const isCompleted = a.status.toLowerCase() === "completed";
          const isFuture = a.status.toLowerCase() === "scheduled" && new Date(a.date) >= today;
          const color = isCompleted ? "#4caf50" : isMissed ? "#d32f2f" : isFuture ? "#1976d2" : "#bdbdbd";
          const label = isCompleted ? "✓" : isMissed ? "✗" : isFuture ? "·" : "○";
          const dateStr = new Date(a.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
          const tip = `${dateStr} — ${a.status}${a.sessionComments ? ": " + a.sessionComments.slice(0, 80) : ""}`;
          return (
            <span key={i} title={tip} style={{
              width: 24, height: 24, borderRadius: "50%",
              background: color, display: "flex", alignItems: "center", justifyContent: "center",
              color: "#fff", fontSize: 10, fontWeight: 800, cursor: "default", flexShrink: 0,
              boxShadow: isCompleted ? "0 1px 4px rgba(76,175,80,0.4)" : isMissed ? "0 1px 4px rgba(211,47,47,0.3)" : "none",
            }}>
              {label}
            </span>
          );
        })}
      </div>

      {/* Progress bar */}
      <div style={{ height: 10, borderRadius: 5, background: "#e0e0e0", overflow: "hidden" }}>
        <div style={{
          height: "100%", width: `${pct}%`,
          background: pct >= 70 ? "#4caf50" : pct >= 40 ? "#ff9800" : "#f44336",
          borderRadius: 5, transition: "width 0.6s ease",
        }} />
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#888", marginTop: 4 }}>
        <span>{pct}% completion rate</span>
        <span style={{ color: missedCount > 0 ? "#d32f2f" : "#4caf50" }}>
          {missedCount} missed · {completedSessions} completed
        </span>
      </div>
    </div>
  );
}

/* ===============================
   COMM ITEM
================================ */
function CommItem({ item }) {
  const dateStr = item.date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });

  if (item.kind === "session") {
    const isCompleted = item.status === "completed";
    return (
      <div style={{
        padding: "7px 10px", borderRadius: 6, fontSize: 12,
        background: isCompleted ? "#f0fdf4" : "#fff0f0",
        borderLeft: `3px solid ${isCompleted ? "#4caf50" : "#d32f2f"}`,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: item.text ? 3 : 0 }}>
          <span style={{ fontWeight: 700 }}>{isCompleted ? "✅" : "❌"} Session — {dateStr}</span>
          {item.by && <span style={{ color: "#999", fontSize: 11 }}>{item.by}</span>}
        </div>
        {item.text
          ? <span style={{ color: "#444" }}>{item.text.slice(0, 160)}{item.text.length > 160 ? "…" : ""}</span>
          : !isCompleted && <span style={{ color: "#d32f2f", fontStyle: "italic" }}>No session notes recorded</span>
        }
      </div>
    );
  }

  if (item.kind === "whatsapp") {
    return (
      <div style={{
        padding: "7px 10px", borderRadius: 6, fontSize: 12,
        background: "#f0fdf9", borderLeft: "3px solid #25D366",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: item.text ? 3 : 0 }}>
          <span style={{ fontWeight: 700 }}>💬 WhatsApp — {dateStr}</span>
          {item.msgType && <span style={{ color: "#999", fontSize: 11 }}>{item.msgType}</span>}
        </div>
        {item.text && <span style={{ color: "#444" }}>{item.text.slice(0, 140)}{item.text.length > 140 ? "…" : ""}</span>}
      </div>
    );
  }

  if (item.kind === "followup") {
    return (
      <div style={{
        padding: "7px 10px", borderRadius: 6, fontSize: 12,
        background: "#fffde7", borderLeft: "3px solid #fbc02d",
      }}>
        <div style={{ fontWeight: 700, marginBottom: item.text ? 3 : 0 }}>📋 Follow-up Note — {dateStr}</div>
        {item.text && <span style={{ color: "#444" }}>{item.text.slice(0, 140)}{item.text.length > 140 ? "…" : ""}</span>}
      </div>
    );
  }

  return null;
}

/* ── shared micro-styles ── */
const sectionTitleStyle = {
  fontSize: 11, fontWeight: 700, color: "#666",
  textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, display: "block",
};

const actionBtnStyle = (bg) => ({
  padding: "4px 12px", background: bg, color: "#fff",
  borderRadius: 6, textDecoration: "none", fontSize: 13, fontWeight: 700,
  display: "inline-flex", alignItems: "center", gap: 4,
});

/* ===============================
   STYLES
================================ */
const styles = {
  summaryRow: {
    display: "flex",
    gap: 12,
    marginBottom: 16,
    flexWrap: "wrap"
  },
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  modal: {
    background: "#fff",
    padding: 20,
    width: 440,
    borderRadius: 8,
    maxHeight: "85vh",
    overflowY: "auto"
  }
};
